import { BlogPost, BlogCategory, BlogMetadata } from '../types/blog';

// Custom reading time calculation (browser-compatible) - Fixed
function calculateReadingTime(text: string) {
  const wordsPerMinute = 200;
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  
  return {
    text: `${minutes} min read`,
    minutes,
    time: minutes * 60 * 1000,
    words
  };
}

// Simple frontmatter parser (browser-compatible)
function parseFrontmatter(content: string): { data: any; content: string } {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);
  
  if (!match) {
    return { data: {}, content };
  }
  
  const [, frontmatter, markdownContent] = match;
  const data: any = {};
  
  // Parse YAML-like frontmatter
  const lines = frontmatter.split('\n');
  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;
    
    const key = line.substring(0, colonIndex).trim();
    let value = line.substring(colonIndex + 1).trim();
    
    // Remove quotes if present
    if ((value.startsWith('"') && value.endsWith('"')) || 
        (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    
    // Parse arrays (tags)
    if (value.startsWith('[') && value.endsWith(']')) {
      const items = value.slice(1, -1).split(',').map(item =>
        item.trim().replace(/^["']|["']$/g, '')
      );
      data[key] = items;
    } else if (value === 'true') {
      // Parse booleans
      data[key] = true;
    } else if (value === 'false') {
      data[key] = false;
    } else {
      data[key] = value;
    }
  }
  
  return { data, content: markdownContent.trim() };
}

// Blog posts stored as static markdown files
const blogPosts = [
  {
    slug: 'web-accessibility-2025',
    filename: '01-web-accessibility-2025.md'
  },
  {
    slug: '10-haeufigste-accessibility-fehler',
    filename: '02-10-haeufigste-accessibility-fehler.md'
  }
];

export async function getAllPosts(): Promise<BlogPost[]> {
  const posts: BlogPost[] = [];
  
  for (const post of blogPosts) {
    try {
      console.log(`Fetching blog post: /blog-posts/${post.filename}`);
      const response = await fetch(`/blog-posts/${post.filename}`);
      
      if (!response.ok) {
        console.error(`Failed to fetch ${post.filename}: ${response.status} ${response.statusText}`);
        continue;
      }
      
      const content = await response.text();
      console.log(`Fetched content length for ${post.filename}: ${content.length} characters`);
      
      // Check if we got HTML instead of markdown (common issue with Vite)
      if (content.startsWith('<!DOCTYPE') || content.startsWith('<html')) {
        console.error(`Got HTML instead of markdown for ${post.filename}. This suggests the file path is wrong.`);
        continue;
      }
      
      const { data, content: markdownContent } = parseFrontmatter(content);
      console.log(`Parsed frontmatter for ${post.filename}:`, data);
      
      const metadata = data as BlogMetadata;
      const timeToRead = calculateReadingTime(markdownContent);
      
      // Handle category as string and map to enum
      let category: BlogCategory = BlogCategory.FUNDAMENTALS;
      if (typeof metadata.category === 'string') {
        // Find matching enum value
        const categoryMatch = Object.values(BlogCategory).find(
          cat => cat === metadata.category
        );
        if (categoryMatch) {
          category = categoryMatch;
        } else {
          console.warn(`Unknown category "${metadata.category}", using default`);
        }
      }
      
      posts.push({
        slug: post.slug,
        title: metadata.title,
        description: metadata.description,
        author: metadata.author || 'Accessibility Team',
        date: metadata.date,
        category: category,
        tags: metadata.tags || [],
        readingTime: timeToRead,
        featured: metadata.featured || false,
        image: metadata.image,
        content: markdownContent
      });
      
      console.log(`Successfully loaded blog post: ${post.slug}`);
    } catch (error) {
      console.error(`Error loading blog post ${post.slug}:`, error);
    }
  }
  
  console.log(`Total blog posts loaded: ${posts.length}`);
  
  // Sort by date (newest first)
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  const posts = await getAllPosts();
  return posts.find(post => post.slug === slug) || null;
}

export async function getPostsByCategory(category: BlogCategory): Promise<BlogPost[]> {
  const posts = await getAllPosts();
  return posts.filter(post => post.category === category);
}

export async function getFeaturedPosts(): Promise<BlogPost[]> {
  const posts = await getAllPosts();
  return posts.filter(post => post.featured);
}

export async function getRelatedPosts(currentPost: BlogPost, limit: number = 3): Promise<BlogPost[]> {
  const posts = await getAllPosts();
  
  // Filter out current post
  const otherPosts = posts.filter(post => post.slug !== currentPost.slug);
  
  // Score posts based on similarity
  const scoredPosts = otherPosts.map(post => {
    let score = 0;
    
    // Same category = 10 points
    if (post.category === currentPost.category) score += 10;
    
    // Shared tags = 5 points per tag
    const sharedTags = post.tags.filter(tag => currentPost.tags.includes(tag));
    score += sharedTags.length * 5;
    
    return { post, score };
  });
  
  // Sort by score and return top matches
  return scoredPosts
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.post);
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function getCategoryColor(category: BlogCategory): string {
  const colors: Record<BlogCategory, string> = {
    [BlogCategory.FUNDAMENTALS]: 'bg-blue-100 text-blue-800',
    [BlogCategory.PRACTICAL_GUIDES]: 'bg-green-100 text-green-800',
    [BlogCategory.TOOLS]: 'bg-purple-100 text-purple-800',
    [BlogCategory.BUSINESS]: 'bg-orange-100 text-orange-800',
    [BlogCategory.LEGAL]: 'bg-red-100 text-red-800',
    [BlogCategory.FRAMEWORKS]: 'bg-indigo-100 text-indigo-800'
  };
  
  return colors[category] || 'bg-gray-100 text-gray-800';
}